// The four approval workflows. Each maps to a `*_WORKFLOW` field (whose values
// are the ordered approver chain) plus sibling response-template and analyzer
// fields. Terms & conditions for each workflow live in the `conditions` relation
// (not yet wired — see section copy).

export type WorkflowKey = 'program' | 'courses' | 'evidence' | 'withdraw';

export interface WorkflowDef {
  key: WorkflowKey;
  /** i18n keys for the workflow name + description (resolved in components). */
  nameKey: string;
  descKey: string;
  workflowField: string;
  approveTextField: string;
  reviseTextField: string;
  rejectTextField: string;
  analyzerField: string;
  /** `condition.province` for this workflow's Terms & Conditions. */
  conditionProvince: string;
  /**
   * Field holding the calendar-day count after which the step is approved as it stands.
   * Absent for workflows without a deadline rule.
   */
  autoApproveDaysField?: string;
}

export const WORKFLOWS: WorkflowDef[] = [
  {
    key: 'program',
    nameKey: 'program_builder.workflows.program.name',
    descKey: 'program_builder.workflows.program.desc',
    workflowField: 'PROGRAM_APPROVE_WORKFLOW',
    approveTextField: 'PROGRAM_APPROVAL_APPROVE_TEXT',
    reviseTextField: 'PROGRAM_APPROVAL_REVISE_TEXT',
    rejectTextField: 'PROGRAM_APPROVAL_REJECT_TEXT',
    analyzerField: 'PROGRAM_APPROVAL_ANALYZER_INSTRUCTION_SET',
    conditionProvince: 'program_approve_workflow',
    autoApproveDaysField: 'PROGRAM_APPROVAL_AUTO_APPROVE_AFTER_DAYS',
  },
  {
    key: 'courses',
    nameKey: 'program_builder.workflows.courses.name',
    descKey: 'program_builder.workflows.courses.desc',
    workflowField: 'APPLICATION_APPROVE_COURSES_WORKFLOW',
    approveTextField: 'COURSES_APPROVAL_APPROVE_TEXT',
    reviseTextField: 'COURSES_APPROVAL_REVISE_TEXT',
    rejectTextField: 'COURSES_APPROVAL_REJECT_TEXT',
    analyzerField: 'COURSES_APPROVAL_ANALYZER_INSTRUCTION_SET',
    conditionProvince: 'application_approve_courses_workflow',
    autoApproveDaysField: 'COURSES_APPROVAL_AUTO_APPROVE_AFTER_DAYS',
  },
  {
    key: 'evidence',
    nameKey: 'program_builder.workflows.evidence.name',
    descKey: 'program_builder.workflows.evidence.desc',
    workflowField: 'APPLICATION_APPROVE_EVIDENCE_WORKFLOW',
    approveTextField: 'EVIDENCE_APPROVAL_APPROVE_TEXT',
    reviseTextField: 'EVIDENCE_APPROVAL_REVISE_TEXT',
    rejectTextField: 'EVIDENCE_APPROVAL_REJECT_TEXT',
    analyzerField: 'EVIDENCE_APPROVAL_ANALYZER_INSTRUCTION_SET',
    conditionProvince: 'application_approve_evidence_workflow',
    autoApproveDaysField: 'EVIDENCE_APPROVAL_AUTO_APPROVE_AFTER_DAYS',
  },
  {
    key: 'withdraw',
    nameKey: 'program_builder.workflows.withdraw.name',
    descKey: 'program_builder.workflows.withdraw.desc',
    workflowField: 'PROGRAM_APPROVE_WITHDRAW_COMPLETE_WORKFLOW',
    approveTextField: 'WITHDRAW_COMPLETE_APPROVAL_APPROVE_TEXT',
    reviseTextField: 'WITHDRAW_COMPLETE_APPROVAL_REVISE_TEXT',
    rejectTextField: 'WITHDRAW_COMPLETE_APPROVAL_REJECT_TEXT',
    analyzerField: 'WITHDRAW_COMPLETE_APPROVAL_ANALYZER_INSTRUCTION_SET',
    conditionProvince: 'program_approve_withdraw_complete_workflow',
  },
];

const ALLOWED_BY_TYPE: Record<string, WorkflowKey[]> = {
  'TAS.ProgramType.4.3': ['program', 'withdraw', 'courses', 'evidence'],
  'TAS.ProgramType.4.2': ['program', 'withdraw', 'evidence'],
  'TAS.ProgramType.3': ['courses', 'evidence'],
  'TAS.ProgramType.2': ['evidence'],
  // Tracking-Only: the two PROGRAM-level workflows (Program Approval to join +
  // Withdraw/Mark-Complete). No course/evidence (APPLICATION-level) workflows.
  'TAS.ProgramType.1': ['program', 'withdraw'],
};

/** Which workflows show for a program type (its allowed set). */
export function workflowsFor(typeKey: string): WorkflowDef[] {
  const allowed = new Set<WorkflowKey>(ALLOWED_BY_TYPE[typeKey] ?? []);
  return WORKFLOWS.filter((workflow) => allowed.has(workflow.key));
}
